import { ItemGroup } from 'rc-menu';
import type {
  MenuDividerType as RcMenuDividerType,
  MenuItemGroupType as RcMenuItemGroupType,
  MenuItemType as RcMenuItemType,
  SubMenuType as RcSubMenuType,
} from 'rc-menu/lib/interface';
import * as React from 'react';
import { TooltipPlacement } from '../tooltip';
import MenuDivider from './components/menu-divider';
import MenuItem from './components/menu-item';
import SubMenu from './components/sub-menu';

export interface MenuItemType extends Omit<RcMenuItemType, 'itemIcon'> {
  variant?: 'default' | 'danger' | 'success';
  Icon?: (p: React.SVGProps<SVGSVGElement>) => JSX.Element;
  title?: string;
  tooltip?: React.ReactNode;
  tooltipPlacement?: TooltipPlacement;
}

export interface SubMenuType extends Omit<RcSubMenuType, 'children' | 'itemIcon'> {
  Icon?: (p: React.SVGProps<SVGSVGElement>) => JSX.Element;
  children: ItemType[];
}

export interface MenuItemGroupType extends Omit<RcMenuItemGroupType, 'children' | 'itemIcon'> {
  children?: ItemType[];
  key?: React.Key;
}

export interface MenuDividerType extends RcMenuDividerType {
  dashed?: boolean;
  key?: React.Key;
}

export type ItemType = MenuItemType | SubMenuType | MenuItemGroupType | MenuDividerType | null;

// https://github.com/ant-design/ant-design/blob/master/components/menu/hooks/useItems.tsx
function convertItemsToNodes(list: ItemType[]) {
  return (list || [])
    .map((opt, index) => {
      if (opt && typeof opt === 'object') {
        const { label, children, key, type, ...restProps } = opt as any;
        const mergedKey = key ?? `tmp-${index}`;

        // MenuItemGroup & SubMenuItem
        if (children || type === 'group') {
          if (type === 'group') {
            // Group
            return (
              <ItemGroup key={mergedKey} {...restProps} title={label}>
                {convertItemsToNodes(children)}
              </ItemGroup>
            );
          }

          // Sub Menu
          return (
            <SubMenu key={mergedKey} {...restProps} title={label}>
              {convertItemsToNodes(children)}
            </SubMenu>
          );
        }

        // MenuItem & Divider
        if (type === 'divider') {
          return <MenuDivider key={mergedKey} {...restProps} />;
        }

        return (
          <MenuItem key={mergedKey} {...restProps}>
            {label}
          </MenuItem>
        );
      }

      return null;
    })
    .filter((opt) => opt);
}

// FIXME: Move logic here in v5
/**
 * We simply convert `items` to ReactNode for reuse origin component logic. But we need move all the
 * logic from component into this hooks when in v5
 */
export default function useItems(items?: ItemType[]) {
  return React.useMemo(() => {
    if (!items) {
      return items;
    }

    return convertItemsToNodes(items);
  }, [items]);
}
